/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';

import { environment } from '@env/environment';

/**
 * Service for logging messages to the console in non-production environments.
 * 
 * @remarks
 * This service provides wrapper methods around console logging functions that
 * only execute in non-production environments. All logging is automatically
 * suppressed when `environment.production` is true.
 * 
 * The console will automatically show clickable links to the source files.
 */
@Injectable({
  providedIn: 'root',
})
export class LoggerService {
  //#region Methods

  /**
   * Logs messages to the console in non-production environments.
   * @param args - Variable number of arguments to be logged to the console
   */
  log(...args: any[]): void {
    this.write(console.log, ...args);
  }

  /**
   * Logs an error message to the console in non-production environments.
   * @param args - Variable number of arguments to be logged as error output
   */
  error(...args: any[]): void {
    this.write(console.error, ...args);
  }

  /**
   * Logs a warning message to the console in non-production environments.
   * @param args - The arguments to log as a warning message
   */
  warn(...args: any[]): void {
    this.write(console.warn, ...args);
  }

  /**
   * Logs informational messages to the console.
   * @param args - The arguments to be logged to the console
   */
  info(...args: any[]): void {
    this.write(console.info, ...args);
  }

  //#endregion

  //#region Helpers

  /**
   * Writes messages to the console in non-production environments.
   * The browser will automatically display the caller location as a clickable link.
   * @param logFn - The console logging function to use
   * @param args - Variable number of arguments to be logged
   */
  private write(logFn: (...data: any[]) => void, ...args: any[]): void {
    if (!environment.production) {
      logFn(...args);
    }
  }

  //#endregion
}